import { Typography } from "@mui/material";
import "../../components/PageComponents/OrderComponents/OrderConfirmationPageComponents/OrderConfirmationPage.scss";
import plantIcon from "../../assets/images/order_page/plant.svg";
// import shieldImg from "../../assets/images/order_page/security_shield.svg";
import { useLocation } from "react-router-dom";
import { ErrorLogger } from "../../utils/Logger";
import { useEffect, useRef, useState } from "react";
import GGLoaderView from '../../components/UtilityComponents/GGLoaderView/GGLoaderView';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useNavigate } from 'react-router-dom';
import { SessionManager } from "../../services/SessionManager/SessionManager";
import { getDateFromTimeStamp } from "../../utils/DateAndTimeUtils";
import { OrderAPIHandler } from "../../services/NetworkClient/BackendAPIs/OrderAPIHandler";
import { Order } from "../../Models/Order";
import SnackBar, { SnackTypes } from "../../components/UtilityComponents/SnackBar/SnackBar";
import CustomMessageDeliveryHandler from "../../services/ChatManager/CustomMessageDeliveryHandler";
import { OrderProposalActionType } from "../../components/PageComponents/ChatComponents/GGCustomMessageTypes";
import { formatCurrencyIndian } from "../../utils/MoneyConversionUtils";
import { ActionCard } from "../../Models/ActionCard";
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';
import Navbar from "../../components/CommonComponents/Navbar/Navbar";
import AddSpace from "../../components/UtilityComponents/AddSpace/AddSpace";
import { ShoppingCart } from "@mui/icons-material";

interface LocationState {
  from?: { pathname: string };
  actionCardData: ActionCard;
}

const PlaceOrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation() as { state: LocationState };
  const { actionCardData } = location.state;

  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  const PROMOMESSAGES = {
    success: "Promo code applied!",
    failure: "Invalid code",
    info_empty: "Please enter promo code"
  }

  const [showLoader, setShowLoader] = useState(false);

  const [order, setOrder] = useState<Order | undefined>(undefined)
  const [paymentInstance, setPaymentInstance] = useState<any | undefined>(undefined)
  const [enteredPromoCode, setEnteredPromoCode] = useState('');
  const [applyPromoMessage, setApplyPromoMessage] = useState('');

  useEffect(() => {
    document.title = "GenieGig | Order Confirmation";
    logAnalyticsEvent(GGAnalyticsEvents.CREATE_ORDER_PAGE_LOADED);
    performOrderCheckoutInit()
  },
    // eslint-disable-next-line
    []);

  const performOrderCheckoutInit = async () => {
    setShowLoader(true)
    try {
      const { order, paymentInstance } = await OrderAPIHandler.shared.placeOrder(actionCardData.id, enteredPromoCode)
      setPaymentInstance(paymentInstance)
      setOrder(order)
      handlePromoMessageChanges(order)
    } catch (err) {
      ErrorLogger.log(err, 'OrderConfirmationPage.performOrderCheckoutInit');
      showSnackbar(err as string, SnackTypes.error);
    } finally {
      setShowLoader(false)
    }
  }

  const handlePromoMessageChanges = (updatedOrder: Order) => {
    const promoEnteredByuser = enteredPromoCode
    const promoPresentInCalculatedOrder = updatedOrder?.orderSummary?.primaryTransaction.promoDetails?.promoCode

    // remove promocode message if user has not eneterd any promo code himself
    if (promoEnteredByuser === "") {
      setApplyPromoMessage('')
    }
    else if (promoEnteredByuser === promoPresentInCalculatedOrder) {
      // Since promocode got applied successfully
      setApplyPromoMessage(PROMOMESSAGES.success);
    } else {
      // if user provided a promocode but backend didn't apply it means it was invalid 
      setApplyPromoMessage(PROMOMESSAGES.failure);
    }
  }

  function resetPromoDiscountsWithPromoMessage(msg: string) {
    setApplyPromoMessage(msg);
  }

  const applyPromoCode = () => {
    if (enteredPromoCode !== "")
      performOrderCheckoutInit()
    else
      resetPromoDiscountsWithPromoMessage(PROMOMESSAGES.info_empty);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const sendOrderProposalActionTakenCustomMessage = (order: Order) => {
    try {
      const customMessageDeliveryHandler = new CustomMessageDeliveryHandler(order.metaData.channelId);
      let actionType = OrderProposalActionType.accepted
      customMessageDeliveryHandler.sendOrderProposalActionTakenCustomMessage(actionType, {
        buyerId: order.metaData.buyer.id,
        sellerId: order.metaData.seller.id
      })
    } catch (error) {
      ErrorLogger.log(error, 'OrderConfirmationPage.sendOrderProposalActionTakenCustomMessage');
    }
  }

  const initPayment = (data: any, order: Order) => {
    let buyer = SessionManager.shared.buyer;
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID ?? "rzp_test_0ZCJ4koe7Wewfx",
      amount: data.amount,
      currency: data.currency,
      name: "GenieGig",
      description: "Order placement payment of gig on GenieGig for order id: " + order.id,
      order_id: data.id,

      handler: async function (response: any) {
        // Sending the response to backend to verify the payment
        setShowLoader(true)
        try {
          const { paymentSuccess, order: updatedOrder } = await OrderAPIHandler.shared.verifyPlacedOrder(response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature, order.id)
          if (paymentSuccess) {
            sendOrderProposalActionTakenCustomMessage(order);
            navigate("/payment-success", { state: { order: updatedOrder } });
          } else
            navigate("/payment-failure");
        } catch (error) {
          navigate("/payment-failure");
        } finally {
          setShowLoader(false)
        }

      },

      prefill: {
        name: buyer?.fullName,
        email: buyer?.email,
        contact: buyer?.mobileNo,
      },
      notes: {
        address: "E-322, Tagore Garden Extension, New Delhi - 110027",
        order_id: order.id,
      },
      theme: {
        color: "#000000",
      },
    };

    const razorPayInstance = new (window as any).Razorpay(options);
    razorPayInstance.open();
  };

  const handlePayment = async () => {
    try {
      await loadRazorpayScript();
      if (!order) {
        const { order, paymentInstance } = await OrderAPIHandler.shared.placeOrder(actionCardData.id)
        initPayment(paymentInstance, order);
      } else {
        initPayment(paymentInstance, order);
      }

    } catch (err) {
      ErrorLogger.log(err, 'OrderConfirmationPage.handlePaymentFlow');
      showSnackbar(err as string, SnackTypes.error);
    }
  };

  return (
    <div className="order-confirmation-page">
      {showLoader && <GGLoaderView />}
      <AddSpace vSpace={"7vh"} hSpace={"0vh"} />
      <Navbar useScrollVersion={true} />
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={6000} message={snackBarParams.message} />
      {order &&
        <div className="root-div">
          <div className="header-div">
            <ShoppingCart />
            <h2 className="order-confirmation-text">Order Confirmation</h2>
          </div>
          <div className="body">
            <div className="left-div">
              {/* Gig Details Section */}
              <div className="section-container">
                <Typography className="section-title" variant='h6' fontWeight={700}>Gig Details</Typography>
                <div className="section-content">
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Title</Typography>
                    <Typography className="info-value" variant="body1">{order.metaData.gig.title}</Typography>
                  </div>

                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Rating</Typography>
                    <Typography className="info-value" variant="body1">
                      {order.metaData.gig.rating ? `${order.metaData.gig.rating.toFixed(1)} ⭐ (${order.metaData.gig.noOfRatings} reviews)` : 'No ratings yet'}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Freelancer Information Section */}
              <div className="section-container">
                <Typography className="section-title" variant='h6' fontWeight={700}>Freelancer Information</Typography>
                <div className="section-content">
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Name</Typography>
                    <Typography className="info-value" variant="body1">{order.metaData.seller.fullName}</Typography>
                  </div>

                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Location</Typography>
                    <Typography className="info-value" variant="body1">
                      {order.metaData.seller.location.state}, {order.metaData.seller.location.country}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Delivery Details Section */}
              <div className="section-container">
                <Typography className="section-title" variant='h6' fontWeight={700}>Delivery Details</Typography>
                <div className="section-content">
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Deliverables</Typography>
                    <Typography className="info-value" variant="body1">
                      {actionCardData.orderDetails.deliverables && actionCardData.orderDetails.deliverables.map((item: string, index: number) => (
                        <span key={index} className="deliverable-item">{item}</span>
                      ))}
                    </Typography>
                  </div>
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Delivery By</Typography>
                    <Typography className="info-value" variant="body1">{getDateFromTimeStamp(Date.now() + actionCardData.orderDetails.deliveryDurationEpoch)}</Typography>
                  </div>
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Delivery Duration</Typography>
                    <Typography className="info-value" variant="body1">
                      {Math.ceil(actionCardData.orderDetails.deliveryDurationEpoch / (1000 * 60 * 60 * 24))} days
                    </Typography>
                  </div>
                  {/* Remarks section can come in handy in future use */}
                  {/* {actionCardData.orderDetails.remarks && (
                    <div className="info-row">
                      <Typography className="info-label" variant='subtitle2' fontWeight={600}>Special Instructions</Typography>
                      <Typography className="info-value" variant="body2">{actionCardData.orderDetails.remarks}</Typography>
                    </div>
                  )} */}
                </div>
              </div>

              {/* Revision Details Section */}
              <div className="section-container">
                <Typography className="section-title" variant='h6' fontWeight={700}>Revision Details</Typography>
                <div className="section-content">
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Included Revisions</Typography>
                    <Typography className="info-value" variant="body1">{actionCardData.orderDetails.noOfRevisions}</Typography>
                  </div>
                  <div className="info-row">
                    <Typography className="info-label" variant='subtitle2' fontWeight={600}>Additional Revision Cost</Typography>
                    <Typography className="info-value" variant="body1">{formatCurrencyIndian(actionCardData.orderDetails.revisionCharge)}</Typography>
                  </div>
                </div>
              </div>

              <img className="plant-img" src={plantIcon} height={50} alt="img load failed"></img>
            </div>
            <div className="right-div">
              <div className="price-info-grid">
                <div className="price-info-grid-item-left"><Typography variant='subtitle1'>Gig Amount</Typography></div>
                <div className="price-info-grid-item-right">{formatCurrencyIndian(order?.orderSummary?.primaryTransaction.basePrice ?? 0)}</div>
                <div className="price-info-grid-item-left">
                  <Typography variant='subtitle1'>Platform Charges ({(order?.orderSummary?.primaryTransaction.buyerFinancials.platformFee.percentage ?? 0.50) * 100}%) </Typography>
                </div>
                <div className="price-info-grid-item-right">
                  {formatCurrencyIndian(order?.orderSummary?.primaryTransaction.buyerFinancials.platformFee.actualFeeCharged ?? 0)}
                </div>
                <div className="price-info-grid-item-left"><Typography variant='subtitle1'>GST @ {(order?.orderSummary?.primaryTransaction.buyerFinancials.gstDetails.ratePercentage ?? 0) * 100}% (on platform charges)</Typography></div>
                <div className="price-info-grid-item-right">{formatCurrencyIndian(order?.orderSummary?.primaryTransaction.buyerFinancials.gstDetails.actualGstAmount ?? 0)}</div>

                {order?.orderSummary?.primaryTransaction.promoDetails?.promoCode && (
                  <>
                    <div className="price-info-grid-item-left" ><Typography variant='subtitle1'>Promo Applied ({order?.orderSummary?.primaryTransaction.promoDetails?.promoCode})</Typography></div>
                    <div className="price-info-grid-item-right promo-code">- {formatCurrencyIndian(order?.orderSummary?.primaryTransaction.promoDetails?.actualDiscountValue)}</div>
                  </>
                )}
              </div>

              <hr></hr>

              <div className="price-details-div">
                <div className="price-info-grid">
                  <div className="price-info-grid-item-left"><Typography fontWeight={1000} variant='h6'>Grand Total</Typography></div>
                  <div className="price-info-grid-item-right">
                    <Typography fontWeight={1000} variant='h6'>
                      {formatCurrencyIndian(order?.orderSummary?.primaryTransaction.buyerFinancials.totalAmountPayable ?? 0)}
                    </Typography></div>
                </div>
              </div>

              <div className="price-details-div">
                <div className="price-info-grid">
                  <input
                    className="promo-text-input"
                    type="text"
                    placeholder="Enter Promo Code"
                    onChange={(event) => {
                      let enteredText = event.target.value.toString()
                      setEnteredPromoCode(enteredText.toUpperCase());
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter')
                        applyPromoCode()
                    }}
                  >
                  </input>
                  <button className="apply-promo-button" onClick={applyPromoCode}>Apply</button>
                </div>
              </div>

              {applyPromoMessage !== "" && (
                <>
                  <div className="promo-code-message-div">
                    {applyPromoMessage === PROMOMESSAGES.success && (<><CheckCircleOutlineIcon style={{ color: 'green' }}></CheckCircleOutlineIcon></>)}
                    {applyPromoMessage === PROMOMESSAGES.failure && (<><CancelOutlinedIcon style={{ color: 'maroon' }}></CancelOutlinedIcon></>)}
                    {applyPromoMessage === PROMOMESSAGES.info_empty && (<><InfoOutlinedIcon style={{ color: 'lightblue' }}></InfoOutlinedIcon></>)}
                    <Typography className="promo-apply-message" variant='subtitle1'>{applyPromoMessage}</Typography>
                  </div>
                </>
              )}

              {/* Promo List will be introduced in future */}
              {/* <div className="promotions-list">
                <PromoList promotions={[...PROMOTIONCODES, ...PROMOTIONCODES]} handleApply={applyPromoCode}/>
              </div> */}

              <div className="bottom-div">
                <div className="payment-security-warning">
                  {/* <WarningAmberIcon className="warning-icon" /> */}
                  <VerifiedUserRoundedIcon className="warning-icon" />
                  <div className="warning-content">
                    <Typography className="warning-title" variant="h6" fontWeight={700}>
                      Payment Security Notice
                    </Typography>
                    <Typography className="warning-text" variant="body1">
                      <strong>Your payment will only be released to the freelancer once you approve and accept the completed order. </strong>
                      Until then, your payment is <span className="highlight-text">Completely Secure</span> with GenieGig and will be automatically refunded in case of any discrepancy or dispute.
                    </Typography>
                  </div>
                </div>

                {/* <div className="security-message-box">
                  <img className="shield-img" src={shieldImg} height={80} alt="img load failed"></img>
                  <Typography align={'justify'} className="security-text">Your payment is secure untill you recieve all your deliverables in your account</Typography>
                </div> */}
                <button className="proceed-to-pay-btn" onClick={handlePayment}> Proceed to pay </button>
              </div>

            </div>
          </div>
        </div>
      }

    </div>
  );
};

export default PlaceOrderPage;
